import statisticsApi from './statisticsApi';
import type { StartedProcess } from 'components/ProcessItem';
import dayjs from 'dayjs';
import { checkUserDataSortType, SortCriterion } from 'common/utilities';
import { SortState } from 'hooks/useSortableList';
import type {
  StatisticsV2Response,
  StatisticsV2HistoryResponse,
  StatisticsV2HistoryProcess,
  StatisticsV2HistoryTask,
  StatisticsV2CurrentParams,
  StatisticsV2HistoryParams,
  StatisticsV2Status,
  StatisticsV2Task,
} from 'model/statisticsModel';

export const STATISTICS_V2_PROCESS_PAGE_SIZE = 20;
export const STATISTICS_V2_METADATA_PAGE_SIZE = 500;

/**
 * The keys the server knows how to sort by, in the order `sortProcesses()`
 * used to check them. That order is fixed, not the order the user clicked in.
 */
const SORT_KEY_ORDER = [
  'activity',
  'model',
  'processmodelname',
  'process',
  'activityStart',
  'priorityorder',
  'timeindoing',
  'processduration',
  'timetotakeover',
] as const;

/** `newSort` key -> `sortby` value on the wire. Only `activityStart` differs. */
const SORT_KEY_TO_API: Record<string, string> = {
  activity: 'activity',
  model: 'model',
  processmodelname: 'processmodelname',
  process: 'process',
  activityStart: 'activitystart',
  priorityorder: 'priorityorder',
  timeindoing: 'timeindoing',
  processduration: 'processduration',
  timetotakeover: 'timetotakeover',
};

export interface ApiSortParams {
  sortby?: string[];
  sortdir?: string[];
  userdatakey?: string[];
  userdatatype?: string[];
}

const directionOf = (state: SortState): string =>
  state === SortState.Asc ? 'asc' : 'desc';

/**
 * Translate the local sort state into the API's four aligned lists.
 *
 * This is a port of `sortProcesses()`, not a redesign — it has to keep sorting
 * the same way, so two of its quirks are reproduced deliberately:
 *
 * 1. The known keys are checked in a fixed order and only the **first active
 *    one** is applied. Clicking a second column therefore does not add a
 *    secondary sort; it replaces the first.
 * 2. With no known key active, `sortProcesses()` falls through to a loop over
 *    `Object.keys(newSort)` that re-sorts the whole array once per key. Each
 *    sort is stable, so the last key ends up dominant — the criteria are
 *    emitted reversed to match.
 *
 * `processes` is only read to detect the declared type of a userdata key, the
 * same way the old local sort did.
 */
export const newSortToApiSort = (
  newSort: SortCriterion,
  processes: StartedProcess[] = [],
): ApiSortParams => {
  for (const key of SORT_KEY_ORDER) {
    if (newSort[key]) {
      return {
        sortby: [SORT_KEY_TO_API[key]],
        sortdir: [directionOf(newSort[key])],
      };
    }
  }

  const keys = Object.keys(newSort)
    .filter((key) => newSort[key])
    .reverse();
  if (keys.length === 0) return {};

  const userdatas = processes.map((process) => process.process_userdata);
  return {
    sortby: keys.map(() => 'userdata'),
    sortdir: keys.map((key) => directionOf(newSort[key])),
    userdatakey: keys,
    // checkUserDataSortType returns the numeric SupportedUserDataTypes enum;
    // the backend accepts both that and the names.
    userdatatype: keys.map((key) =>
      String(checkUserDataSortType(userdatas, newSort[key], key)),
    ),
  };
};

export interface ProcessMonitorFilterValues {
  status?: string;
  modelids?: number[];
  activityids?: string[];
  userids?: string[];
  groupids?: string[];
  roleids?: string[];
  creatorids?: number[];
  search?: string;
  userDataKeys?: string[];
  tags?: string[];
  groups?: string[];
  roles?: string[];
  priorities?: string[];
  dateRange?: { start: dayjs.Dayjs | null; end: dayjs.Dayjs | null };
}

interface ParticipantUser {
  id: number;
  groups: { id: number }[];
  roles: { id: number }[];
}

const hasParticipantFilter = (values: ProcessMonitorFilterValues) =>
  Boolean(values.userids?.length || values.groupids?.length || values.roleids?.length);

export const resolveParticipantUserIds = (
  values: ProcessMonitorFilterValues,
  users: ParticipantUser[],
): number[] | undefined => {
  let candidateIds: number[] | null = null;

  const merge = (ids: number[]) => {
    candidateIds = candidateIds === null ? ids : candidateIds.filter((id) => ids.includes(id));
  };

  if (values.userids?.length) {
    merge(values.userids.map(Number));
  }
  if (values.groupids?.length) {
    merge(
      users
        .filter((user) => user.groups.some((group) => values.groupids!.includes(String(group.id))))
        .map((user) => user.id),
    );
  }
  if (values.roleids?.length) {
    merge(
      users
        .filter((user) => user.roles.some((role) => values.roleids!.includes(String(role.id))))
        .map((user) => user.id),
    );
  }

  return candidateIds ?? undefined;
};

export const buildStatisticsV2Params = (
  values: ProcessMonitorFilterValues,
  users: ParticipantUser[],
  history: boolean,
  page = 1,
  sort: ApiSortParams = {},
): StatisticsV2CurrentParams | StatisticsV2HistoryParams => {
  const params: StatisticsV2CurrentParams = {
    page,
    pagesize: STATISTICS_V2_PROCESS_PAGE_SIZE,
  };

  if (!history && values.status) {
    params.status = values.status as StatisticsV2Status;
  }
  if (values.modelids?.length) params.modelids = values.modelids;
  if (values.activityids?.length) params.activityids = values.activityids;

  const userids = resolveParticipantUserIds(values, users);
  if (userids?.length) params.userids = userids;

  if (values.creatorids?.length) params.creatorids = values.creatorids;

  if (values.tags?.length) params.tags = values.tags;
  if (values.groups?.length) params.groups = values.groups;
  if (values.roles?.length) params.roles = values.roles;
  if (values.priorities?.length) params.priorities = values.priorities;
  if (values.userDataKeys?.length) params.userDataKeys = values.userDataKeys;
  if (values.search?.trim()) params.search = values.search.trim();

  // The four lists travel together: they are aligned by position, so sending
  // `sortby` without its companions would shift every userdata criterion.
  if (sort.sortby?.length) {
    params.sortby = sort.sortby;
    params.sortdir = sort.sortdir ?? [];
    params.userdatakey = sort.userdatakey ?? [];
    params.userdatatype = sort.userdatatype ?? [];
  }

  if (history && values.dateRange?.start && values.dateRange?.end) {
    return {
      ...params,
      datefrom: values.dateRange.start.toISOString(),
      dateto: values.dateRange.end.toISOString(),
    };
  }

  return params;
};

export const shouldSkipStatisticsV2Fetch = (
  values: ProcessMonitorFilterValues,
  users: ParticipantUser[],
): boolean => {
  if (!hasParticipantFilter(values)) return false;
  const userids = resolveParticipantUserIds(values, users);
  return userids !== undefined && userids.length === 0;
};

const serializeJsonField = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

export const mapStatisticsV2TaskToStartedProcess = (
  task: StatisticsV2Task,
): StartedProcess => ({
  idprocesstoken: task.idprocesstoken,
  idprocess: task.idprocess,
  iduser: task.iduser ?? 0,
  state: task.state,
  processmodelname: task.processmodelname,
  created: task.created,
  done: task.done,
  flownodename: task.flownodename,
  idflownode: 0,
  startdoing: task.startdoing,
  userschema: '',
  designerid: task.designerid ?? '',
  user_picture: null,
  process_description: task.process_description ?? '',
  descriptiondetail: task.process_description_detail ?? null,
  process_enddate: task.process_enddate,
  process_startdate: task.process_startdate,
  process_idprocessmodel: task.process_idprocessmodel,
  process_userdata: task.process_userdata ?? '',
  processduration: task.processduration ?? 0,
  processmodeldeleted: false,
  flownodetype: task.flownodetype,
  idtype: task.idtype,
  previous: 0,
  taskduration: task.timeindoing,
  timeindoing: task.timeindoing ?? 0,
  timetotakeover: task.timetotakeover ?? 0,
  userfullname: task.userfullname,
  jsontag: serializeJsonField(task.jsontag),
  jsongroups: serializeJsonField(task.jsongroups),
  jsonroles: serializeJsonField(task.jsonroles),
  priorityname: task.priorityname,
  priorityorder: task.priorityorder,
});

export const mapStatisticsV2TasksToStartedProcesses = (
  tasks: StatisticsV2Task[],
): StartedProcess[] => tasks.map(mapStatisticsV2TaskToStartedProcess);

const groupTasksByProcess = (tasks: StartedProcess[]): StartedProcess[] => {
  const grouped = new Map<number, StartedProcess[]>();
  tasks.forEach((task) => {
    if (!grouped.has(task.idprocess)) {
      grouped.set(task.idprocess, []);
    }
    grouped.get(task.idprocess)!.push(task);
  });
  return Array.from(grouped.values()).map((taskList) => ({
    ...taskList[0],
    tasks: taskList,
  }));
};

const buildFullTaskFromHistoryProcess = (
  process: StatisticsV2HistoryProcess,
  task: StatisticsV2HistoryTask,
): StatisticsV2Task => ({
  idprocesstoken: task.idprocesstoken,
  idprocess: process.idprocess,
  iduser: task.iduser,
  state: task.state,
  process_idprocessmodel: process.process_idprocessmodel,
  processmodelname: process.processmodelname,
  flownodename: task.flownodename,
  flownodetype: task.flownodetype,
  idtype: task.idtype,
  process_startdate: process.process_startdate,
  process_enddate: process.process_enddate,
  created: task.created,
  startdoing: task.startdoing,
  done: task.done,
  process_description: process.process_description,
  process_description_detail: process.process_description_detail,
  process_userdata: process.process_userdata,
  jsontag: process.jsontag,
  jsongroups: process.jsongroups,
  jsonroles: process.jsonroles,
  priorityname: process.priorityname,
  priorityorder: process.priorityorder,
  timeindoing: task.timeindoing,
  timetotakeover: task.timetotakeover,
  processduration: process.processduration,
  designerid: process.designerid,
  userfullname: task.userfullname,
});

export const isStatisticsV2HistoryProcessResponse = (
  data: unknown[],
): data is StatisticsV2HistoryProcess[] =>
  data.length === 0
  || (
    typeof data[0] === 'object'
    && data[0] !== null
    && 'tasks' in data[0]
    && Array.isArray((data[0] as StatisticsV2HistoryProcess).tasks)
  );

export const mapStatisticsV2HistoryProcessToStartedProcess = (
  process: StatisticsV2HistoryProcess,
): StartedProcess | null => {
  const mappedTasks = (process.tasks ?? []).map((task) =>
    mapStatisticsV2TaskToStartedProcess(buildFullTaskFromHistoryProcess(process, task)),
  );
  if (mappedTasks.length === 0) return null;
  return {
    ...mappedTasks[0],
    tasks: mappedTasks,
  };
};

export const mapStatisticsV2HistoryProcessesToStartedProcesses = (
  processes: StatisticsV2HistoryProcess[],
): StartedProcess[] =>
  processes
    .map(mapStatisticsV2HistoryProcessToStartedProcess)
    .filter((process): process is StartedProcess => process !== null);

/** Maps history API data to grouped process rows (new shape) or legacy flat tasks. */
export const mapHistoryV2ResponseToStartedProcesses = (
  data: StatisticsV2HistoryProcess[] | StatisticsV2Task[],
): StartedProcess[] => {
  if (isStatisticsV2HistoryProcessResponse(data)) {
    return mapStatisticsV2HistoryProcessesToStartedProcesses(data);
  }
  return groupTasksByProcess(mapStatisticsV2TasksToStartedProcesses(data));
};

const serializeParams = (params: Record<string, any>): string => {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) sp.append(key, value.join(','));
    } else {
      sp.append(key, String(value));
    }
  }
  return sp.toString();
};

export const fetchStatisticsV2Current = async (
  organizationId: number,
  params: StatisticsV2CurrentParams = {},
): Promise<StatisticsV2Response> => {
  const { data } = await statisticsApi.get<StatisticsV2Response>(
    `/v2/current/${organizationId}?${serializeParams(params)}`,
  );
  return data;
};

export const fetchStatisticsV2History = async (
  organizationId: number,
  params: StatisticsV2HistoryParams = {},
): Promise<StatisticsV2HistoryResponse | StatisticsV2Response> => {
  const { data } = await statisticsApi.get<StatisticsV2HistoryResponse | StatisticsV2Response>(
    `/v2/history/${organizationId}?${serializeParams(params)}`,
  );
  return data;
};
